import axios from 'axios';
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Typography, Box, Divider, Button, Grid} from "@mui/material";
import { useMsal } from '@azure/msal-react';
import Markdown from 'react-markdown'
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import MuiAccordion from '@mui/material/Accordion';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import { styled } from '@mui/material/styles';
import SyncLoader from "react-spinners/SyncLoader";

const Accordion = styled((props) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  '&:not(:last-child)': {
    borderBottom: 0,
  },
  '&::before': {
    display: 'none',
  },
}));

const AccordionSummary = styled((props) => (
  <MuiAccordionSummary
    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem' }} />}
    {...props}
  />
))(({ theme }) => ({
  backgroundColor: 'rgba(0, 0, 0, .03)',
  flexDirection: 'row-reverse',
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)',
  },
  '& .MuiAccordionSummary-content': {
    marginLeft: theme.spacing(1),
  },
  ...theme.applyStyles('dark', {
    backgroundColor: 'rgba(255, 255, 255, .05)',
  }),
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: '1px solid rgba(0, 0, 0, .125)',
}));

function AdditionalInfo({ document, isMissingInfo }){
  if (isMissingInfo) {
    return (          
      <div>
        <Typography variant="h6" sx={{ mt: 2 }}>Missing Info</Typography>
        <Typography variant="body1">
          The letter is missing crucial information.
        </Typography>
        <Typography variant="body1">
          <Typography variant="h6" sx={{ mt: 2 }}>Follow Up Letter Draft</Typography>
          <Markdown>
            {document.processed_information.missing_info.next_steps}
          </Markdown>
        </Typography>
      </div> 
    )
  }
  return (          
    <div>
      <Typography variant="h6" sx={{ mt: 2 }}>Criteria Check</Typography>
      <Typography variant="body1">
        <strong>Evaluation:</strong>
        <Markdown>
          {document.processed_information.criteria_check.evaluation}
        </Markdown>
      </Typography>
      <Typography variant="body1">
        <strong>Conclusion:</strong>
        <Markdown>
          {document.processed_information.criteria_check.conclusion}
        </Markdown>
      </Typography>
      <Typography variant="body1">
        <strong>Recommended Next Steps:</strong>
        <Markdown>
          {document.processed_information.criteria_check.next_steps}
        </Markdown>
      </Typography>
    </div> 
  )
}

function DocumentPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [documentData, setDocumentData] = useState(null);
  const [documentUrl, setDocumentUrl] = useState(null); 
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { instance, accounts } = useMsal();

  useEffect(() => {
    const fetchDocument = async (instance) => {
      try {
        const token_response = await instance.acquireTokenSilent({...{scopes: ["User.Read"]}, account: accounts[0]})
        const headers =  {
          "Content-Type": "application/json",
          "Authorization": "Bearer " + token_response.accessToken,
        }
        const response = await axios.get(`https://openai-intake-app.azurewebsites.net/v1/document/${id}`, {
          headers: headers,
        });
        console.log(response.data)
        setDocumentData(response.data);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching document:", err);
        setError(err);
        setLoading(false);
      }

      try {
        const token_response = await instance.acquireTokenSilent({...{scopes: ["User.Read"]}, account: accounts[0]})
        const headers =  {
          "Content-Type": "application/json",
          "Authorization": "Bearer " + token_response.accessToken,
        }
        const response = await axios.get(`https://openai-intake-app.azurewebsites.net/v1/document/pdf/${id}`, {
          headers: headers,
          responseType: 'blob'
        });

        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        setDocumentUrl(url);
      } catch (err) {
        console.error('Error fetching document:', err);
      }
    };

    fetchDocument(instance);
  }, [id, instance]);

  const handleBackClick = () => {
    navigate("/documents");
  };

  if (loading) {
    return <div>
          <Grid
      container
      spacing={0}
      direction="column"
      alignItems="center"
      justifyContent="center"
      sx={{ minHeight: '100vh' }}

    >
      <Grid container item xs={3} width="60%" justifyContent="center" alignItems="center" >
      <SyncLoader
          color="#236ECA"
          loading={loading}
          size={20}
          aria-label="Loading Spinner"
          data-testid="loader"
        />
      </Grid>
    </Grid>
    </div>;
  }

  if (error) {
    return <div>Error loading documents: {error.message}</div>;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", width: "80%", p: 2 }}>
      <Button
        variant="contained"
        color="primary"
        onClick={handleBackClick}
        sx={{ alignSelf: "flex-start", mb: 2 }}
      >
        Go back
      </Button>
      <Box sx={{ display: "flex", width: "100%" }}>
        {/* original_document */}
        <Box sx={{ width: "50%", p: 2 }}>
        <Typography variant="h6">Document</Typography>
          <Typography variant="body1">
            <iframe src={documentUrl} width="500" height="800"></iframe>
            {/* <iframe src={documentData.original_document_url} width="400" height="800"></iframe> */}
            {/* <a href={documentData.original_document_url} target="_blank" rel="noopener noreferrer">
              Open PDF
            </a> */}
          </Typography>
          <Accordion slotProps={{ heading: { component: 'h4' } }}>
            <AccordionSummary
              expandIcon={<ArrowForwardIosSharpIcon />}
              aria-controls="panel1-content"
              id="panel1-header"
            >
              Transcription
            </AccordionSummary>
            <AccordionDetails>
              <Markdown>
                {documentData.processed_information.transcribed_text}
              </Markdown>
            </AccordionDetails>
          </Accordion>
        </Box>

        <Divider orientation="vertical" flexItem />

        {/* processed_information */}
        <Box sx={{ width: "50%", p: 2 }}>
          <Typography variant="h6">Summary</Typography>
          <Typography variant="body1">
            <Markdown>
              {documentData.processed_information.background_info}
            </Markdown>
          </Typography>
            <AdditionalInfo document={documentData} isMissingInfo={documentData.processed_information.missing_info.response.toLowerCase() == "yes" ? true : false} />
        </Box>
      </Box>
    </Box>
  );
}

export default DocumentPage;
